/*
 * Ported from the IDV proof of concept (frontend/src/utils/cameraQuality.ts).
 *
 * Mechanical TypeScript -> JavaScript conversion (tsc --target ES2020, types
 * erased). The algorithms, thresholds and geometry are unchanged: this file is
 * pinned by src/utils/documentCrop.test.js and by the IDV backend contract.
 */

/* global globalThis */
/* `globalThis` is ES2020 and available in every browser this app targets;
   CRA 5's eslint env predates it, so it is declared here rather than
   changing the shared lint config or the ported logic. */
const DOCUMENT_MIN_SHARPNESS_BAD = 250;
const DOCUMENT_MIN_SHARPNESS_STRICT = 450;
const DOCUMENT_MIN_CONTRAST_BAD = 5;
const DOCUMENT_MIN_CONTRAST_STRICT = 25;
const TOO_CLOSE_WIDTH_RATIO = 0.82;
const TOO_CLOSE_AREA_RATIO = 0.68;
const OPTIMAL_MIN_WIDTH_RATIO = 0.5;
const OPTIMAL_MAX_WIDTH_RATIO = 0.75;
export function getDefaultDocumentQualityThresholds() {
    return {
        minSharpness: 45,
        minBrightness: 42,
        maxBrightness: 222,
        maxGlareRatio: 0.08,
        minContrast: 16,
    };
}
export function getDocumentQualityLevel(score) {
    if (!score)
        return null;
    if (score.sharpness < DOCUMENT_MIN_SHARPNESS_BAD ||
        score.brightness < 20 ||
        score.contrast < DOCUMENT_MIN_CONTRAST_BAD ||
        score.warnings.includes('too_dark') ||
        score.warnings.includes('too_bright')) {
        return 'bad';
    }
    if (score.sharpness < DOCUMENT_MIN_SHARPNESS_STRICT ||
        score.contrast < DOCUMENT_MIN_CONTRAST_STRICT) {
        return 'weak';
    }
    return 'good';
}
export function scoreCanvasFrame(canvas, thresholds = getDefaultDocumentQualityThresholds()) {
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context || canvas.width < 3 || canvas.height < 3) {
        return {
            sharpness: 0,
            brightness: 0,
            glareRatio: 0,
            contrast: 0,
            passed: false,
            warnings: ['too_blurry'],
        };
    }
    const image = context.getImageData(0, 0, canvas.width, canvas.height);
    const { data } = image;
    const pixelCount = canvas.width * canvas.height;
    const grayscale = new Float32Array(pixelCount);
    let brightnessSum = 0;
    let glarePixels = 0;
    for (let pixelIndex = 0; pixelIndex < pixelCount; pixelIndex += 1) {
        const dataIndex = pixelIndex * 4;
        const red = data[dataIndex];
        const green = data[dataIndex + 1];
        const blue = data[dataIndex + 2];
        const gray = 0.299 * red + 0.587 * green + 0.114 * blue;
        grayscale[pixelIndex] = gray;
        brightnessSum += gray;
        if (red > 245 && green > 245 && blue > 245) {
            glarePixels += 1;
        }
    }
    const brightness = brightnessSum / pixelCount;
    let varianceSum = 0;
    for (let index = 0; index < grayscale.length; index += 1) {
        const delta = grayscale[index] - brightness;
        varianceSum += delta * delta;
    }
    const contrast = Math.sqrt(varianceSum / pixelCount);
    let laplacianSum = 0;
    let laplacianSquaredSum = 0;
    let laplacianCount = 0;
    for (let y = 1; y < canvas.height - 1; y += 1) {
        for (let x = 1; x < canvas.width - 1; x += 1) {
            const centerIndex = y * canvas.width + x;
            const laplacian = 4 * grayscale[centerIndex] -
                grayscale[centerIndex - 1] -
                grayscale[centerIndex + 1] -
                grayscale[centerIndex - canvas.width] -
                grayscale[centerIndex + canvas.width];
            laplacianSum += laplacian;
            laplacianSquaredSum += laplacian * laplacian;
            laplacianCount += 1;
        }
    }
    const laplacianMean = laplacianCount ? laplacianSum / laplacianCount : 0;
    const sharpness = laplacianCount
        ? laplacianSquaredSum / laplacianCount - laplacianMean * laplacianMean
        : 0;
    const glareRatio = glarePixels / pixelCount;
    const warnings = [];
    if (sharpness < thresholds.minSharpness)
        warnings.push('too_blurry');
    if (brightness < thresholds.minBrightness)
        warnings.push('too_dark');
    if (brightness > thresholds.maxBrightness)
        warnings.push('too_bright');
    if (glareRatio > thresholds.maxGlareRatio)
        warnings.push('too_much_glare');
    if (contrast < thresholds.minContrast)
        warnings.push('low_contrast');
    return {
        sharpness: roundMetric(sharpness),
        brightness: roundMetric(brightness),
        glareRatio: roundMetric(glareRatio),
        contrast: roundMetric(contrast),
        passed: warnings.length === 0,
        warnings,
    };
}
export function scoreVideoFrame(video, options) {
    if (!video.videoWidth || !video.videoHeight)
        return null;
    const sourceWidth = video.videoWidth;
    const sourceHeight = video.videoHeight;
    const roi = options?.roi ??
        centralRoi(sourceWidth, sourceHeight, {
            widthRatio: 0.72,
            heightRatio: 0.58,
        });
    const maxWidth = options?.maxWidth ?? 420;
    const scale = Math.min(1, maxWidth / roi.width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(16, Math.round(roi.width * scale));
    canvas.height = Math.max(16, Math.round(roi.height * scale));
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context)
        return null;
    context.drawImage(video, roi.x, roi.y, roi.width, roi.height, 0, 0, canvas.width, canvas.height);
    return scoreCanvasFrame(canvas, options?.thresholds);
}
export function estimateDocumentDistanceFromPreview(video, options) {
    if (!video.videoWidth || !video.videoHeight)
        return null;
    const tooCloseWidthRatio = options?.tooCloseWidthRatio ?? TOO_CLOSE_WIDTH_RATIO;
    const tooFarWidthRatio = options?.tooFarWidthRatio ?? 0.42;
    const optimalMinWidthRatio = options?.optimalMinWidthRatio ?? OPTIMAL_MIN_WIDTH_RATIO;
    const optimalMaxWidthRatio = options?.optimalMaxWidthRatio ?? OPTIMAL_MAX_WIDTH_RATIO;
    const maxWidth = 320;
    const scale = Math.min(1, maxWidth / video.videoWidth);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(16, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(16, Math.round(video.videoHeight * scale));
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context)
        return null;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const image = context.getImageData(0, 0, canvas.width, canvas.height);
    const grayscale = new Float32Array(canvas.width * canvas.height);
    const { data } = image;
    let brightnessSum = 0;
    for (let pixelIndex = 0; pixelIndex < grayscale.length; pixelIndex += 1) {
        const dataIndex = pixelIndex * 4;
        const gray = 0.299 * data[dataIndex] +
            0.587 * data[dataIndex + 1] +
            0.114 * data[dataIndex + 2];
        grayscale[pixelIndex] = gray;
        brightnessSum += gray;
    }
    const brightness = brightnessSum / grayscale.length;
    let varianceSum = 0;
    for (let index = 0; index < grayscale.length; index += 1) {
        const delta = grayscale[index] - brightness;
        varianceSum += delta * delta;
    }
    const contrast = Math.sqrt(varianceSum / grayscale.length);
    const edgeThreshold = Math.max(24, contrast * 1.15);
    let minX = canvas.width;
    let minY = canvas.height;
    let maxX = 0;
    let maxY = 0;
    let edgePixels = 0;
    const marginX = Math.round(canvas.width * 0.04);
    const marginY = Math.round(canvas.height * 0.04);
    for (let y = marginY + 1; y < canvas.height - marginY - 1; y += 1) {
        for (let x = marginX + 1; x < canvas.width - marginX - 1; x += 1) {
            const centerIndex = y * canvas.width + x;
            const gx = -grayscale[centerIndex - canvas.width - 1] +
                grayscale[centerIndex - canvas.width + 1] -
                2 * grayscale[centerIndex - 1] +
                2 * grayscale[centerIndex + 1] -
                grayscale[centerIndex + canvas.width - 1] +
                grayscale[centerIndex + canvas.width + 1];
            const gy = -grayscale[centerIndex - canvas.width - 1] -
                2 * grayscale[centerIndex - canvas.width] -
                grayscale[centerIndex - canvas.width + 1] +
                grayscale[centerIndex + canvas.width - 1] +
                2 * grayscale[centerIndex + canvas.width] +
                grayscale[centerIndex + canvas.width + 1];
            const magnitude = Math.sqrt(gx * gx + gy * gy);
            if (magnitude < edgeThreshold)
                continue;
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
            edgePixels += 1;
        }
    }
    const warnings = [];
    const minEdgePixels = canvas.width * canvas.height * 0.004;
    if (edgePixels < minEdgePixels || maxX <= minX || maxY <= minY) {
        return {
            detected: false,
            documentWidthRatio: null,
            documentHeightRatio: null,
            documentAreaRatio: null,
            tooClose: false,
            tooFar: false,
            optimalDistance: false,
            warnings: ['document_contour_not_detected'],
        };
    }
    const boxWidth = maxX - minX;
    const boxHeight = maxY - minY;
    const documentWidthRatio = boxWidth / canvas.width;
    const documentHeightRatio = boxHeight / canvas.height;
    const documentAreaRatio = (boxWidth * boxHeight) / (canvas.width * canvas.height);
    const aspectRatio = boxWidth / Math.max(1, boxHeight);
    if (aspectRatio < 0.9 || aspectRatio > 2.8 || documentAreaRatio < 0.04) {
        const ratioSuggestsTooClose = documentWidthRatio >= tooCloseWidthRatio;
        const areaSuggestsTooClose = documentAreaRatio >= TOO_CLOSE_AREA_RATIO;
        const tooClose = ratioSuggestsTooClose || areaSuggestsTooClose;
        const unreliableWarnings = ['document_contour_unreliable'];
        if (tooClose) {
            unreliableWarnings.push('too_close_even_if_contour_unreliable');
        }
        return {
            detected: false,
            documentWidthRatio: roundMetric(documentWidthRatio),
            documentHeightRatio: roundMetric(documentHeightRatio),
            documentAreaRatio: roundMetric(documentAreaRatio),
            tooClose,
            tooFar: false,
            optimalDistance: false,
            warnings: unreliableWarnings,
        };
    }
    const ratioSuggestsTooClose = documentWidthRatio >= tooCloseWidthRatio;
    const areaSuggestsTooClose = documentAreaRatio >= TOO_CLOSE_AREA_RATIO;
    const tooClose = ratioSuggestsTooClose || areaSuggestsTooClose;
    const tooFar = documentWidthRatio < tooFarWidthRatio;
    const optimalDistance = documentWidthRatio >= optimalMinWidthRatio &&
        documentWidthRatio <= optimalMaxWidthRatio;
    if (tooClose)
        warnings.push('document_too_close');
    if (tooFar)
        warnings.push('document_too_far');
    return {
        detected: true,
        documentWidthRatio: roundMetric(documentWidthRatio),
        documentHeightRatio: roundMetric(documentHeightRatio),
        documentAreaRatio: roundMetric(documentAreaRatio),
        tooClose,
        tooFar,
        optimalDistance,
        warnings,
    };
}
/**
 * Downscale a crop region of an image source into a small `size`×`size`,
 * mean-subtracted grayscale signature suitable for structural comparison.
 *
 * Used only to compare the visual STRUCTURE of two crops (preview vs native
 * mapping); it deliberately throws away resolution and does not attempt to read
 * personal data. Returns null when the crop or canvas is unusable.
 */
export function extractCropGraySignature(source, crop, size = 32) {
    if (!(crop.width > 0) || !(crop.height > 0) || size < 2)
        return null;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context)
        return null;
    try {
        context.drawImage(source, crop.x, crop.y, crop.width, crop.height, 0, 0, size, size);
    }
    catch {
        return null;
    }
    const { data } = context.getImageData(0, 0, size, size);
    const signature = new Float32Array(size * size);
    let sum = 0;
    for (let pixelIndex = 0; pixelIndex < signature.length; pixelIndex += 1) {
        const dataIndex = pixelIndex * 4;
        const gray = 0.299 * data[dataIndex] +
            0.587 * data[dataIndex + 1] +
            0.114 * data[dataIndex + 2];
        signature[pixelIndex] = gray;
        sum += gray;
    }
    const mean = sum / signature.length;
    for (let index = 0; index < signature.length; index += 1) {
        signature[index] -= mean;
    }
    return signature;
}
export async function scoreImageBlob(blob, thresholds) {
    try {
        if ('createImageBitmap' in globalThis) {
            const bitmap = await createImageBitmap(blob);
            const score = scoreDecodedImage(bitmap, bitmap.width, bitmap.height, thresholds);
            bitmap.close();
            return score;
        }
        const image = await loadImage(blob);
        return scoreDecodedImage(image, image.naturalWidth, image.naturalHeight, thresholds);
    }
    catch {
        return null;
    }
}
function centralRoi(width, height, ratios) {
    const roiWidth = width * ratios.widthRatio;
    const roiHeight = height * ratios.heightRatio;
    return {
        x: (width - roiWidth) / 2,
        y: (height - roiHeight) / 2,
        width: roiWidth,
        height: roiHeight,
    };
}
function roundMetric(value) {
    return Math.round(value * 1000) / 1000;
}
function scoreDecodedImage(image, width, height, thresholds) {
    if (!width || !height)
        return null;
    const roi = centralRoi(width, height, {
        widthRatio: 0.72,
        heightRatio: 0.58,
    });
    const maxWidth = 720;
    const scale = Math.min(1, maxWidth / roi.width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(16, Math.round(roi.width * scale));
    canvas.height = Math.max(16, Math.round(roi.height * scale));
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context)
        return null;
    context.drawImage(image, roi.x, roi.y, roi.width, roi.height, 0, 0, canvas.width, canvas.height);
    return scoreCanvasFrame(canvas, thresholds);
}
function loadImage(blob) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        const objectUrl = URL.createObjectURL(blob);
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('image_decode_failed'));
        };
        image.src = objectUrl;
    });
}
